import fixtures from './fixtures.json';

/* Kitxens Demo Mode — anonymized sample data, zero real records exposed. */

export const isDemo = () => {
  try { return sessionStorage.getItem('kx_demo') === '1'; } catch { return false; }
};
export const enterDemo = () => { try { sessionStorage.setItem('kx_demo', '1'); } catch { /* noop */ } };
export const exitDemo = () => { try { sessionStorage.removeItem('kx_demo'); } catch { /* noop */ } };

export const demoProfile = () => {
  try { return JSON.parse(sessionStorage.getItem('kx_demo_profile') || '{}'); } catch { return {}; }
};
export const saveDemoProfile = (p) => { try { sessionStorage.setItem('kx_demo_profile', JSON.stringify(p)); } catch { /* noop */ } };

export function demoFixture(path) {
  const clean = path.split('?')[0];
  const fx = fixtures[clean];
  if (!fx) return null;
  if (clean === '/os2/header') {
    const p = demoProfile();
    const out = { ...fx };
    if (p.name) out.name = p.name.split(' ')[0];
    if (p.city) out.city = p.city;
    return out;
  }
  return fx;
}

/* Smart CRM demo — synthetic health scores + contact timelines, deterministic per email. */
const dhash = (str) => { let h = 0; for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0; return h; };
const dscore = (email) => 28 + (dhash(email) % 68);
const dgrade = (s) => (s >= 75 ? 'hot' : s >= 58 ? 'warm' : s >= 42 ? 'cool' : s >= 32 ? 'cold' : 'at_risk');
const DNEXT = {
  hot: { en: 'Engagement is peaking — book a discovery call today.', es: 'El engagement está en su punto — agenda una llamada de descubrimiento hoy.' },
  warm: { en: 'Follow up on what they clicked — reference it directly in your next email.', es: 'Haz seguimiento de lo que clicó — menciónalo directamente en tu próximo email.' },
  cool: { en: 'Re-engage with a fresh angle or a new channel (WhatsApp/call).', es: 'Reactiva con un ángulo nuevo u otro canal (WhatsApp/llamada).' },
  cold: { en: 'Cold — park in nurture, don\u2019t invest manual time yet.', es: 'Frío — déjalo en nurture, no inviertas tiempo manual todavía.' },
  at_risk: { en: 'Email is failing — switch channel or fix the contact data.', es: 'El email falla — cambia de canal o corrige el contacto.' },
};

function demoTimeline(email) {
  const s = dscore(email);
  const grade = dgrade(s);
  const ago = (h) => new Date(Date.now() - h * 3600e3).toISOString();
  const full = [
    { kind: 'crm_note', title_en: '📌 Note: Call scheduled for Friday 11am — owner very interested', title_es: '📌 Nota: Llamada agendada viernes 11am — owner muy interesado', source: 'crm', ts: ago(3) },
    { kind: 'reply', title_en: 'Replied: "Sounds interesting, send me the details"', title_es: 'Respondió: "Suena interesante, envíame los detalles"', source: 'inbox', ts: ago(26) },
    { kind: 'email_clicked', title_en: 'Clicked: Meet your AI Team', title_es: 'Click: Conoce tu AI Team', source: 'brevo', detail: 'ai-agents', ts: ago(49) },
    { kind: 'email_opened', title_en: 'Opened: Your restaurant, analyzed', title_es: 'Abrió: Tu restaurante, analizado', source: 'brevo', ts: ago(50) },
    { kind: 'email_delivered', title_en: 'Delivered: Your restaurant, analyzed', title_es: 'Entregado: Tu restaurante, analizado', source: 'brevo', ts: ago(51) },
    { kind: 'crm_sync', title_en: 'Synced to CRM · list: Leads', title_es: 'Sincronizado al CRM · lista: Leads', source: 'crm', ts: ago(96) },
    { kind: 'lead', title_en: 'Lead captured · source: website scan', title_es: 'Lead capturado · fuente: escaneo web', source: 'system', ts: ago(120) },
  ];
  const timeline = grade === 'hot' || grade === 'warm' ? full : grade === 'cool' ? full.slice(2) : full.slice(4);
  return { email, count: timeline.length, health: { score: s, grade, next_action: DNEXT[grade] }, timeline };
}

export function demoDynamic(path, method = 'GET') {
  const clean = path.split('?')[0];
  if (method === 'POST' && clean === '/crm/health-scores') {
    const scores = {};
    (fixtures['/os2/sales']?.leads || []).forEach((l) => {
      const s = dscore(l.email);
      const g = dgrade(s);
      scores[l.email] = { score: s, grade: g, next_action: DNEXT[g] };
    });
    return { scores };
  }
  const tm = clean.match(/^\/crm\/contact\/([^/]+)\/timeline$/);
  if (tm) return demoTimeline(decodeURIComponent(tm[1]));
  if (method === 'GET' && clean === '/admin/google-suite/intelligence') return demoGrowthIntelligence();
  if (method === 'GET' && clean === '/admin/ai-workspace/push') return demoPushes();
  return null;
}

/* Rachel Smart Push demo — rating drop + revenue win with quick actions. */
function demoPushes() {
  const at = new Date().toISOString();
  return { pushes: [
    { pid: 'demo-rating-drop', severity: 'warning', icon: '⭐',
      title_en: 'Google rating dropped to 4.4★', title_es: 'La valoración de Google bajó a 4.4★',
      body_en: 'Bella Cucina went from 4.7★ to 4.4★ this week — 3 new negative reviews mention slow service on Friday night. I can ask Linda to draft responses and a recovery plan.',
      body_es: 'Bella Cucina pasó de 4.7★ a 4.4★ esta semana — 3 reseñas negativas nuevas mencionan servicio lento el viernes por la noche. Puedo pedirle a Linda que redacte respuestas y un plan de recuperación.',
      action: { type: 'assign', agent: 'linda',
        task_en: 'Respond to new negative reviews at Bella Cucina and launch 4.7★ recovery plan',
        task_es: 'Responder a las reseñas negativas de Bella Cucina y lanzar plan de recuperación 4.7★',
        label_en: 'Assign to Linda', label_es: 'Asignar a Linda' },
      at },
    { pid: 'demo-revenue-today', severity: 'success', icon: '💳',
      title_en: '$4,280 collected today', title_es: '$4,280 cobrados hoy',
      body_en: 'Best Tuesday of the quarter — up 18% vs. the average. The full breakdown is in Finance.',
      body_es: 'El mejor martes del trimestre — un 18% por encima de la media. El desglose completo está en Finanzas.',
      action: { type: 'navigate', to: '/admin/ai-os/finance',
        label_en: 'Open Finance', label_es: 'Abrir Finanzas' },
      at },
  ] };
}

/* Growth Intelligence demo dataset — 90 daily points with seasonality, anomalies and trends. */
let _growthCache = null;
function demoGrowthIntelligence() {
  if (_growthCache) return _growthCache;
  const day = 86400e3;
  const now = Date.now();
  const trend = [];
  for (let i = 89; i >= 0; i--) {
    const dt = new Date(now - i * day);
    const t = 89 - i;
    const dow = dt.getDay();
    const wk = dow === 5 || dow === 6 ? 1.28 : dow === 0 ? 1.12 : 1.0;
    const users = Math.round((128 + t * 1.9 + Math.sin(t / 9) * 20) * wk);
    const sessions = Math.round(users * 1.34);
    const clicks = Math.round(34 + t * 0.55 + Math.sin(t / 6) * 7);
    const row = { date: dt.toISOString().slice(0, 10), users, sessions, clicks, impressions: clicks * 21 + 850 };
    if (i === 34) { row.users = Math.round(row.users * 2.4); row.sessions = Math.round(row.sessions * 2.3); row.note = 'Featured in local press — traffic spike'; }
    if (i === 12) { row.users = Math.round(row.users * 0.45); row.sessions = Math.round(row.sessions * 0.4); row.note = 'Site slowdown detected (resolved in 6h)'; }
    trend.push(row);
  }
  const last28 = trend.slice(-28);
  const prev28 = trend.slice(-56, -28);
  const sum = (a, k) => a.reduce((s, r) => s + r[k], 0);
  const ga4T = { active_users: sum(last28, 'users'), new_users: Math.round(sum(last28, 'users') * 0.62), sessions: sum(last28, 'sessions'), engaged_sessions: Math.round(sum(last28, 'sessions') * 0.58), pageviews: Math.round(sum(last28, 'sessions') * 2.7), key_events: 218, avg_session_sec: 154, engagement_rate: 58.0 };
  const ga4P = { active_users: sum(prev28, 'users'), sessions: sum(prev28, 'sessions'), key_events: 171, engagement_rate: 52.4 };
  const pct = (c, p) => Math.round(((c - p) / p) * 1000) / 10;
  const Q = (query, clicks, impressions, position, prev) => ({ query, clicks, impressions, ctr: Math.round((clicks / impressions) * 10000) / 100, position, prev_clicks: prev, delta_clicks: clicks - prev });
  const queries = [
    Q('kitxens', 412, 3900, 1.2, 356), Q('ai marketing for restaurants', 188, 4100, 3.4, 121),
    Q('restaurant seo tools', 96, 2900, 5.1, 64), Q('google business profile for restaurants', 84, 3600, 6.2, 58),
    Q('restaurant crm software', 71, 2400, 4.8, 77), Q('ai answer engine restaurants', 64, 1900, 7.9, 31),
    Q('venezuelan catering san francisco', 41, 2430, 8.4, 22), Q('restaurant website analytics', 28, 1600, 9.3, 33),
    Q('menu engineering software', 19, 1200, 12.6, 24),
  ];
  const rising = queries.filter((q) => q.delta_clicks > 0).sort((a, b) => b.delta_clicks - a.delta_clicks).slice(0, 5);
  const falling = queries.filter((q) => q.delta_clicks < 0).sort((a, b) => a.delta_clicks - b.delta_clicks).slice(0, 5);
  const nearP1 = queries.filter((q) => q.position >= 4 && q.position <= 10).sort((a, b) => b.impressions - a.impressions).slice(0, 6);
  const opps = queries.filter((q) => q.impressions >= 1000 && q.ctr < 2.5).map((q) => ({
    ...q, est_extra_clicks: Math.round(q.impressions * Math.max(0, 0.05 - q.ctr / 100)),
    recommendation_en: `“${q.query}” got ${q.impressions.toLocaleString()} impressions but only ${q.ctr}% CTR. Improve the page title and meta description.`,
    recommendation_es: `“${q.query}” generó ${q.impressions.toLocaleString()} impresiones pero solo ${q.ctr}% de CTR. Mejora el título y la meta descripción de la página.`,
  })).sort((a, b) => b.est_extra_clicks - a.est_extra_clicks).slice(0, 5);
  const gscT = { clicks: sum(last28, 'clicks'), impressions: sum(last28, 'impressions'), ctr: 3.1, position: 6.8 };
  const gscP = { clicks: sum(prev28, 'clicks'), impressions: sum(prev28, 'impressions'), ctr: 2.8, position: 7.9 };
  _growthCache = {
    status: 'ok', is_demo: true,
    period: { days: 28, compare: 'previous_28_days' }, fetched_at: new Date().toISOString(),
    sources: {
      gsc: { state: 'operational', property: 'https://kitxens-demo.com/' },
      ga4: { state: 'operational', property: 'Kitxens Demo — GA4' },
      ads: { state: 'operational' }, gbp: { state: 'operational' },
    },
    ga4: {
      property: 'properties/demo', property_name: 'Kitxens Demo — GA4', totals: ga4T, prev: ga4P,
      delta: { active_users: pct(ga4T.active_users, ga4P.active_users), sessions: pct(ga4T.sessions, ga4P.sessions), key_events: pct(ga4T.key_events, ga4P.key_events) },
      channels: [
        { channel: 'Organic Search', sessions: Math.round(ga4T.sessions * 0.41), users: Math.round(ga4T.active_users * 0.4) },
        { channel: 'Direct', sessions: Math.round(ga4T.sessions * 0.22), users: Math.round(ga4T.active_users * 0.23) },
        { channel: 'Paid Search', sessions: Math.round(ga4T.sessions * 0.15), users: Math.round(ga4T.active_users * 0.15) },
        { channel: 'Organic Social', sessions: Math.round(ga4T.sessions * 0.12), users: Math.round(ga4T.active_users * 0.12) },
        { channel: 'Referral', sessions: Math.round(ga4T.sessions * 0.07), users: Math.round(ga4T.active_users * 0.07) },
        { channel: 'Email', sessions: Math.round(ga4T.sessions * 0.03), users: Math.round(ga4T.active_users * 0.03) },
      ],
      trend: last28,
    },
    gsc: {
      site: 'https://kitxens-demo.com/', totals: gscT, prev: gscP,
      delta: { clicks: pct(gscT.clicks, gscP.clicks), impressions: pct(gscT.impressions, gscP.impressions) },
      top_queries: queries.slice().sort((a, b) => b.clicks - a.clicks).slice(0, 8),
      rising, falling, near_page_one: nearP1, low_ctr_opps: opps, trend: last28,
    },
    ads: {
      spend: 2140, impressions: 96400, clicks: 3120, ctr: 3.24, cpc: 0.69, conversions: 118,
      conversion_rate: 3.8, cost_per_conversion: 18.1, revenue: 8620, roas: 4.0,
      campaigns: [
        { name: 'Restaurant OS — Search', spend: 940, clicks: 1480, conversions: 62, roas: 4.8 },
        { name: 'Brand — Kitxens', spend: 310, clicks: 820, conversions: 38, roas: 6.1 },
        { name: 'AI Marketing — Broad', spend: 620, clicks: 640, conversions: 15, roas: 2.1 },
        { name: 'Competitor — Generic', spend: 270, clicks: 180, conversions: 3, roas: 0.7 },
      ],
      wasted_note: {
        en: '“Competitor — Generic” spent $270 for 3 conversions (0.7x ROAS). Pause it or tighten its keywords.',
        es: '“Competitor — Generic” invirtió $270 para 3 conversiones (ROAS 0.7x). Páusala o ajusta sus keywords.',
      },
    },
    gbp: {
      best: 'Casa Mia Trattoria', worst: 'Bistro Verde',
      locations: [
        { name: 'Casa Mia Trattoria', search_impr: 12400, maps_impr: 9800, calls: 312, directions: 618, website_clicks: 941, missing: [] },
        { name: 'Taco Nación', search_impr: 8900, maps_impr: 7100, calls: 204, directions: 402, website_clicks: 566, missing: [] },
        { name: 'Bistro Verde', search_impr: 4100, maps_impr: 2600, calls: 66, directions: 121, website_clicks: 187, missing: ['hours', 'menu link'] },
      ],
    },
    scores: [
      { key: 'digital_growth', name_en: 'Digital Growth Score', name_es: 'Digital Growth Score', value: 78, trend: 'up', confidence: 'high', provisional: false,
        drivers: [
          { label_en: 'Active users +23.4% vs previous period', label_es: 'Usuarios activos +23.4% vs periodo anterior', impact: 14 },
          { label_en: 'Search clicks +19.8%', label_es: 'Clicks de búsqueda +19.8%', impact: 8 },
          { label_en: '218 key events recorded', label_es: '218 eventos clave registrados', impact: 6 }],
        explanation_en: 'Composite of traffic growth, search visibility and conversion activity vs the previous 28 days.',
        explanation_es: 'Compuesto de crecimiento de tráfico, visibilidad en búsqueda y actividad de conversión vs los 28 días anteriores.',
        action_en: 'Double down on the rising queries — publish supporting content this week.',
        action_es: 'Refuerza las búsquedas en crecimiento — publica contenido de apoyo esta semana.' },
      { key: 'seo_opportunity', name_en: 'SEO Opportunity Score', name_es: 'SEO Opportunity Score', value: 71, trend: 'up', confidence: 'high', provisional: false,
        drivers: [
          { label_en: '6 keywords at positions 4–10 (near page one)', label_es: '6 keywords en posiciones 4–10 (cerca de página uno)', impact: 54 },
          { label_en: '11,530 impressions with low CTR to capture', label_es: '11.530 impresiones con CTR bajo por capturar', impact: 17 }],
        explanation_en: 'How much untapped search traffic exists: keywords near page one plus high-impression low-CTR pages.',
        explanation_es: 'Cuánto tráfico de búsqueda sin capturar existe: keywords cerca de página uno más páginas con muchas impresiones y CTR bajo.',
        action_en: 'Improve titles/descriptions of the queries listed under Opportunities.',
        action_es: 'Mejora títulos y descripciones de las búsquedas listadas en Oportunidades.' },
      { key: 'website_conversion', name_en: 'Website Conversion Score', name_es: 'Website Conversion Score', value: 64, trend: 'up', confidence: 'high', provisional: false,
        drivers: [
          { label_en: 'Engagement rate 58%', label_es: 'Tasa de engagement 58%', impact: 32 },
          { label_en: 'Conversion rate 3.8%', label_es: 'Tasa de conversión 3.8%', impact: 30 }],
        explanation_en: 'How well website visits become engaged sessions and key events.',
        explanation_es: 'Qué tan bien las visitas se convierten en sesiones con engagement y eventos clave.',
        action_en: 'Mobile conversion dropped to 2.1% — simplify the mobile demo form.',
        action_es: 'La conversión móvil bajó a 2.1% — simplifica el formulario de demo en móvil.' },
      { key: 'local_visibility', name_en: 'Local Visibility Score', name_es: 'Local Visibility Score', value: 82, trend: 'up', confidence: 'high', provisional: false,
        drivers: [
          { label_en: '25.4K search + 19.5K Maps impressions across 3 locations', label_es: '25.4K impresiones en búsqueda + 19.5K en Maps en 3 locales', impact: 48 },
          { label_en: '582 calls and 1,141 direction requests', label_es: '582 llamadas y 1.141 solicitudes de ruta', impact: 34 }],
        explanation_en: 'Business Profile discovery and actions across all monitored locations.',
        explanation_es: 'Descubrimiento y acciones de Business Profile en todos los locales monitorizados.',
        action_en: 'Bistro Verde is losing visibility — complete its hours and menu link.',
        action_es: 'Bistro Verde está perdiendo visibilidad — completa su horario y el enlace del menú.' },
      { key: 'marketing_efficiency', name_en: 'Marketing Efficiency Score', name_es: 'Marketing Efficiency Score', value: 69, trend: 'flat', confidence: 'medium', provisional: false,
        drivers: [
          { label_en: 'Blended ROAS 4.0x on $2,140 spend', label_es: 'ROAS combinado 4.0x sobre $2,140 de inversión', impact: 40 },
          { label_en: 'Organic contributes 41% of sessions', label_es: 'Lo orgánico aporta el 41% de las sesiones', impact: 22 },
          { label_en: '“Competitor — Generic” wastes $270 at 0.7x ROAS', label_es: '“Competitor — Generic” desperdicia $270 con ROAS 0.7x', impact: -8 }],
        explanation_en: 'Balance between organic growth, paid efficiency and wasted spend.',
        explanation_es: 'Equilibrio entre crecimiento orgánico, eficiencia del pago y gasto desperdiciado.',
        action_en: 'Shift the Competitor budget into “Restaurant OS — Search” (4.8x ROAS).',
        action_es: 'Mueve el presupuesto de Competitor a “Restaurant OS — Search” (ROAS 4.8x).' },
    ],
    narrative: {
      what_happened: {
        es: 'El tráfico orgánico creció 23% en los últimos 28 días, impulsado por búsquedas de “ai marketing for restaurants” y la mención en prensa local del día 19.',
        en: 'Organic traffic grew 23% over the last 28 days, driven by searches for “ai marketing for restaurants” and the local press feature on the 19th.' },
      attention: {
        es: 'El tráfico móvil subió, pero la conversión móvil cayó de 3.4% a 2.1% — el formulario de demo pide demasiados campos en pantallas pequeñas.',
        en: 'Mobile traffic increased, but mobile conversion fell from 3.4% to 2.1% — the demo form asks for too many fields on small screens.' },
      opportunity: {
        es: '6 búsquedas están entre las posiciones 4 y 10 y podrían generar ~310 visitas mensuales adicionales al subir a página uno.',
        en: '5 queries rank between positions 4 and 10 and could generate ~310 additional monthly visits by reaching page one.' },
      next: {
        es: 'Mejora el título y la descripción de la página de “google business profile for restaurants” — 3.600 impresiones con solo 2.3% CTR — y lanza una campaña local sobre “venezuelan catering san francisco”.',
        en: 'Improve the title and description of the “google business profile for restaurants” page — 3,600 impressions at only 2.3% CTR — and launch a local campaign around “venezuelan catering san francisco”.' },
    },
    recommendations: [
      { priority: 'high', source: 'Search Console',
        title_en: 'Push “restaurant seo tools” to page one',
        title_es: 'Lleva “restaurant seo tools” a la página uno',
        detail_en: 'It ranks at position 5.1 with 2,900 impressions — add internal links and refresh the title to win the remaining spots.',
        detail_es: 'Está en posición 5.1 con 2.900 impresiones — añade enlaces internos y renueva el título para ganar las posiciones que faltan.' },
      { priority: 'high', source: 'Search Console',
        title_en: 'Rewrite titles for 3 low-CTR queries (9,000+ impressions)',
        title_es: 'Reescribe títulos de 3 búsquedas con CTR bajo (9.000+ impresiones)',
        detail_en: 'You already show up in Google but few people click. Sharper titles convert those impressions into visits.',
        detail_es: 'Ya apareces en Google pero pocos hacen clic. Títulos más atractivos convierten esas impresiones en visitas.' },
      { priority: 'high', source: 'Google Analytics',
        title_en: 'Fix the mobile demo form — conversion fell to 2.1%',
        title_es: 'Arregla el formulario de demo en móvil — la conversión cayó a 2.1%',
        detail_en: 'Mobile traffic is up but the form asks for too many fields on small screens. Cut it to name + email.',
        detail_es: 'El tráfico móvil sube pero el formulario pide demasiados campos en pantallas pequeñas. Déjalo en nombre + email.' },
      { priority: 'normal', source: 'Google Ads',
        title_en: 'Shift Competitor budget into “Restaurant OS — Search” (4.8x ROAS)',
        title_es: 'Mueve el presupuesto de Competitor a “Restaurant OS — Search” (ROAS 4.8x)',
        detail_en: 'The Competitor campaign burns spend at 0.9x ROAS while Search converts at 4.8x.',
        detail_es: 'La campaña Competitor quema presupuesto a 0.9x ROAS mientras Search convierte a 4.8x.' },
      { priority: 'normal', source: 'Search Console',
        title_en: 'Launch a local page for “venezuelan catering san francisco”',
        title_es: 'Lanza una página local para “venezuelan catering san francisco”',
        detail_en: '2,430 impressions at position 8.4 — a dedicated landing page can own that local demand.',
        detail_es: '2.430 impresiones en posición 8.4 — una landing dedicada puede capturar esa demanda local.' },
    ],
  };
  return _growthCache;
}
